import {
  deleteRecord,
  getAllRecords,
  getRecord,
  getRecords,
  insertRecord,
  updateRecord,
} from "@/utils/helpers/data";
import { TABLES } from "@/utils/constants";

export const COLUMNS = {
  ID: "id",
  NAME: "name",
  DENTIST_ID: "dentist_id",
};

export interface HmoProvider {
  id?: string;
  name: string;
  dentist_id?: string | null;
}

/**
 * Creates a new HMO provider record in the database.
 * @param hmoProvider - The HMO provider data to insert.
 * @returns The created HMO provider or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createHmoProvider = async (
  hmoProvider: HmoProvider,
): Promise<HmoProvider | null> => {
  return insertRecord<HmoProvider>(TABLES.HMO_PROVIDERS, hmoProvider);
};

/**
 * Updates an existing HMO provider record in the database.
 * @param hmoProviderId - The ID of the HMO provider to update.
 * @param hmoProvider - The updated HMO provider data.
 * @returns The updated HMO provider or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updateHmoProvider = async (
  hmoProviderId: string | null | undefined,
  hmoProvider: HmoProvider,
): Promise<HmoProvider | null> => {
  if (!hmoProviderId) return null;
  return updateRecord<HmoProvider>(
    TABLES.HMO_PROVIDERS,
    COLUMNS.ID,
    hmoProviderId,
    hmoProvider,
  );
};

/**
 * Retrieves a single HMO provider by its ID.
 * @param hmoProviderId - The ID of the HMO provider to retrieve.
 * @returns The requested HMO provider or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getHmoProvider = async (
  hmoProviderId: string | null | undefined,
): Promise<HmoProvider | null> => {
  if (!hmoProviderId) return null;
  return getRecord<HmoProvider>(
    TABLES.HMO_PROVIDERS,
    COLUMNS.ID,
    hmoProviderId,
  );
};

/**
 * Retrieves all HMO providers from the database.
 * @returns An array of all HMO providers.
 * @throws Will throw an error if the retrieval fails.
 */
export const getAllHmoProviders = async (): Promise<HmoProvider[]> => {
  return getAllRecords<HmoProvider>(TABLES.HMO_PROVIDERS);
};

/**
 * Retrieves all HMO providers for a specific dentist by the dentist's ID.
 * @param dentistId - The ID of the dentist whose HMO providers to retrieve.
 * @returns An array of HMO providers for the specified dentist.
 * @throws Will throw an error if the retrieval fails.
 */
export const getHmoProvidersByDentistId = async (
  dentistId: string,
): Promise<HmoProvider[]> => {
  return getRecords<HmoProvider>(
    TABLES.HMO_PROVIDERS,
    COLUMNS.DENTIST_ID,
    dentistId,
  );
};

/**
 * Retrieves all HMO provider options from the hmo_provider_options table.
 * @returns An array of HMO provider option names.
 * @throws Will throw an error if the retrieval fails.
 */
export const getHmoProviderOptions = async (): Promise<string[]> => {
  const hmoProviderOptions = await getAllRecords<{ name: string }>(
    TABLES.HMO_PROVIDER_OPTIONS,
  );
  return hmoProviderOptions.map((option) => option.name);
};

/**
 * Deletes an HMO provider by its ID.
 * @param hmoProviderId - The ID of the HMO provider to delete.
 * @returns A boolean indicating whether the deletion was successful.
 * @throws Will throw an error if the deletion fails.
 */
export const deleteHmoProvider = async (
  hmoProviderId: string,
): Promise<void> => {
  return deleteRecord(TABLES.HMO_PROVIDERS, COLUMNS.ID, hmoProviderId);
};
